import React, { useEffect, useState, useRef } from 'react';
import * as THREE from 'three';
import TourControls from './TourControls';
import { ChevronLeftIcon, ChevronRightIcon, ChevronsUpIcon, ZoomInIcon, ZoomOutIcon, RotateCwIcon, HomeIcon } from 'lucide-react';
interface VirtualTourViewerProps {
  fullScreen?: boolean;
}
const VirtualTourViewer: React.FC<VirtualTourViewerProps> = ({
  fullScreen = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isMouseDownRef = useRef<boolean>(false);
  const mousePositionRef = useRef<{
    x: number;
    y: number;
  }>({
    x: 0,
    y: 0
  });
  const rotationRef = useRef<{
    x: number;
    y: number;
  }>({
    x: 0,
    y: 0
  });
  const [currentRoom, setCurrentRoom] = useState<string>('Living Room');
  // Available rooms in the virtual tour
  const rooms = ['Living Room', 'Kitchen', 'Master Bedroom', 'Bathroom'];
  // Room textures - in a real application these would be actual panoramic images of rooms
  const roomTextures = {
    'Living Room': 'https://images.unsplash.com/photo-1600210492493-0946911123ea?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1074&q=80',
    Kitchen: 'https://images.unsplash.com/photo-1556912172-45b7abe8b7e1?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
    'Master Bedroom': 'https://images.unsplash.com/photo-1616594039964-ae9021a400a0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
    Bathroom: 'https://images.unsplash.com/photo-1603825491103-bd638b1873b0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1074&q=80'
  };
  // Initialize the 3D scene
  useEffect(() => {
    if (!containerRef.current) return;
    // Create scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    // Create camera
    const camera = new THREE.PerspectiveCamera(75, containerRef.current.clientWidth / containerRef.current.clientHeight, 0.1, 1000);
    camera.position.z = 0.1;
    cameraRef.current = camera;
    // Create renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true
    });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    // Load the initial room texture
    loadRoomTexture(currentRoom);
    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current || !cameraRef.current || !rendererRef.current) return;
      cameraRef.current.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    };
    window.addEventListener('resize', handleResize);
    // Animation loop
    const animate = () => {
      if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;
      // Update camera rotation based on mouse movement
      if (!isMouseDownRef.current) {
        rotationRef.current.x += 0.001;
      }
      cameraRef.current.rotation.y = rotationRef.current.x;
      cameraRef.current.rotation.x = rotationRef.current.y;
      rendererRef.current.render(sceneRef.current, cameraRef.current);
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    animate();
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (rendererRef.current && containerRef.current) {
        containerRef.current.removeChild(rendererRef.current.domElement);
      }
    };
  }, []);
  // Update scene when room changes
  useEffect(() => {
    loadRoomTexture(currentRoom);
  }, [currentRoom]);
  const loadRoomTexture = (roomName: string) => {
    if (!sceneRef.current) return;
    // Clear existing scene
    while (sceneRef.current.children.length > 0) {
      sceneRef.current.remove(sceneRef.current.children[0]);
    }
    // Create a sphere geometry for the panoramic view
    const geometry = new THREE.SphereGeometry(500, 60, 40);
    // Flip the geometry inside out
    geometry.scale(-1, 1, 1);
    // Load texture for the current room
    const texture = new THREE.TextureLoader().load(roomTextures[roomName]);
    const material = new THREE.MeshBasicMaterial({
      map: texture
    });
    const sphere = new THREE.Mesh(geometry, material);
    sceneRef.current.add(sphere);
  };
  // Mouse event handlers for navigation
  const handleMouseDown = (e: React.MouseEvent) => {
    isMouseDownRef.current = true;
    mousePositionRef.current = {
      x: e.clientX,
      y: e.clientY
    };
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isMouseDownRef.current) return;
    const deltaX = e.clientX - mousePositionRef.current.x;
    const deltaY = e.clientY - mousePositionRef.current.y;
    rotationRef.current.x += deltaX * 0.005;
    rotationRef.current.y = Math.max(Math.min(rotationRef.current.y + deltaY * 0.005, Math.PI / 2), -Math.PI / 2);
    mousePositionRef.current = {
      x: e.clientX,
      y: e.clientY
    };
  };
  const handleMouseUp = () => {
    isMouseDownRef.current = false;
  };
  // Navigation functions
  const handleRoomChange = (direction: 'next' | 'prev') => {
    const currentIndex = rooms.indexOf(currentRoom);
    if (direction === 'next') {
      setCurrentRoom(rooms[(currentIndex + 1) % rooms.length]);
    } else {
      setCurrentRoom(rooms[(currentIndex - 1 + rooms.length) % rooms.length]);
    }
  };
  const handleZoom = (direction: 'in' | 'out') => {
    if (!cameraRef.current) return;
    if (direction === 'in') {
      cameraRef.current.fov = Math.max(cameraRef.current.fov - 5, 30);
    } else {
      cameraRef.current.fov = Math.min(cameraRef.current.fov + 5, 90);
    }
    cameraRef.current.updateProjectionMatrix();
  };
  const resetView = () => {
    if (!cameraRef.current) return;
    rotationRef.current = {
      x: 0,
      y: 0
    };
    cameraRef.current.fov = 75;
    cameraRef.current.updateProjectionMatrix();
  };
  const goToRoom = (roomName: string) => {
    setCurrentRoom(roomName);
  };
  return <div className="relative w-full h-full" data-id="element-810">
      <div ref={containerRef} className="w-full h-full cursor-grab active:cursor-grabbing" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} data-id="element-811" />
      {/* Tour Controls */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-90 rounded-lg shadow-lg p-2 flex items-center space-x-3" data-id="element-812">
        <button onClick={() => handleRoomChange('prev')} className="p-2 hover:bg-gray-100 rounded-full" title="Previous Room" data-id="element-813">
          <ChevronLeftIcon size={20} data-id="element-814" />
        </button>
        <div className="px-2 font-medium text-sm" data-id="element-815">{currentRoom}</div>
        <button onClick={() => handleRoomChange('next')} className="p-2 hover:bg-gray-100 rounded-full" title="Next Room" data-id="element-816">
          <ChevronRightIcon size={20} data-id="element-817" />
        </button>
      </div>
      {/* Control Panel */}
      <div className="absolute top-4 right-4 bg-white bg-opacity-90 rounded-lg shadow-lg p-2" data-id="element-818">
        <div className="flex flex-col space-y-2" data-id="element-819">
          <button onClick={() => handleZoom('in')} className="p-2 hover:bg-gray-100 rounded-full" title="Zoom In" data-id="element-820">
            <ZoomInIcon size={20} data-id="element-821" />
          </button>
          <button onClick={() => handleZoom('out')} className="p-2 hover:bg-gray-100 rounded-full" title="Zoom Out" data-id="element-822">
            <ZoomOutIcon size={20} data-id="element-823" />
          </button>
          <button onClick={resetView} className="p-2 hover:bg-gray-100 rounded-full" title="Reset View" data-id="element-824">
            <RotateCwIcon size={20} data-id="element-825" />
          </button>
          <button onClick={() => goToRoom('Living Room')} className="p-2 hover:bg-gray-100 rounded-full" title="Go to Living Room" data-id="element-826">
            <HomeIcon size={20} data-id="element-827" />
          </button>
        </div>
      </div>
      {/* Room Selection - Only show in full screen mode */}
      {fullScreen && <div className="absolute left-4 top-4 bg-white bg-opacity-90 rounded-lg shadow-lg p-2" data-id="element-828">
          <h3 className="text-sm font-medium px-2 pb-1 border-b mb-1" data-id="element-829">Rooms</h3>
          <div className="flex flex-col space-y-1" data-id="element-830">
            {rooms.map(room => <button key={room} onClick={() => goToRoom(room)} className={`px-3 py-1.5 text-sm rounded text-left ${currentRoom === room ? 'bg-green-100 text-green-800' : 'hover:bg-gray-100'}`} data-id="element-831">
                {room}
              </button>)}
          </div>
        </div>}
      {/* Floor plan - Only show in full screen mode */}
      {fullScreen && <div className="absolute bottom-4 right-4 bg-white bg-opacity-90 rounded-lg shadow-lg p-3 w-48 h-48" data-id="element-832">
          <h3 className="text-sm font-medium mb-1" data-id="element-833">Floor Plan</h3>
          <div className="relative w-full h-36 bg-gray-100 rounded border border-gray-300" data-id="element-834">
            {/* Simplified floor plan visualization */}
            <div className="absolute w-full h-full" data-id="element-835">
              {/* Living Room */}
              <div className={`absolute top-1/4 left-1/4 w-1/3 h-1/3 border ${currentRoom === 'Living Room' ? 'border-green-600 bg-green-100' : 'border-gray-400 bg-white'}`} onClick={() => goToRoom('Living Room')} data-id="element-836">
                <div className="w-full h-full flex items-center justify-center text-xs" data-id="element-837">
                  Living
                </div>
              </div>
              {/* Kitchen */}
              <div className={`absolute top-1/4 right-1/4 w-1/4 h-1/4 border ${currentRoom === 'Kitchen' ? 'border-green-600 bg-green-100' : 'border-gray-400 bg-white'}`} onClick={() => goToRoom('Kitchen')} data-id="element-838">
                <div className="w-full h-full flex items-center justify-center text-xs" data-id="element-839">
                  Kitchen
                </div>
              </div>
              {/* Master Bedroom */}
              <div className={`absolute bottom-1/4 left-1/4 w-1/3 h-1/3 border ${currentRoom === 'Master Bedroom' ? 'border-green-600 bg-green-100' : 'border-gray-400 bg-white'}`} onClick={() => goToRoom('Master Bedroom')} data-id="element-840">
                <div className="w-full h-full flex items-center justify-center text-xs" data-id="element-841">
                  Bedroom
                </div>
              </div>
              {/* Bathroom */}
              <div className={`absolute bottom-1/4 right-1/4 w-1/5 h-1/5 border ${currentRoom === 'Bathroom' ? 'border-green-600 bg-green-100' : 'border-gray-400 bg-white'}`} onClick={() => goToRoom('Bathroom')} data-id="element-842">
                <div className="w-full h-full flex items-center justify-center text-xs" data-id="element-843">
                  Bath
                </div>
              </div>
            </div>
          </div>
        </div>}
      {/* Mobile touch instructions */}
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-90 rounded-lg shadow-lg p-2 text-xs text-center pointer-events-none md:hidden" data-id="element-844">
        Drag to look around
      </div>
    </div>;
};
export default VirtualTourViewer;